/**
 * Reference parsing, normalization, validation, and slug<->reference
 * conversion for the whole Bible.
 *
 * Handles inputs like: "John 3:16", "jn 3:16", "1 Cor 13:4-7", "1co 13 4",
 * "Psalm 23" (chapter-only), "psalm 23:1-6".
 *
 * Slug scheme: {book-slug}-{chapter}[-{startVerse}[-{endVerse}]]
 *   john-3-16, 1-corinthians-13-4-7, psalm-23 (chapter-only)
 */
import {
  BookMeta,
  bookBySlug,
  bookSlugsByLengthDesc,
  isValidChapter,
  isValidVerse,
  resolveBookName,
  verseCount,
} from './bibleBooks';

export interface BibleReference {
  book: BookMeta;
  chapter: number;
  /** undefined = whole-chapter reference (e.g. "Psalm 23") */
  startVerse?: number;
  endVerse?: number;
}

export interface ParseResult {
  ok: true;
  reference: BibleReference;
}
export interface ParseError {
  ok: false;
  reason: 'unrecognized-book' | 'invalid-chapter' | 'invalid-verse' | 'unparseable';
}

/**
 * Parse free-text user input into a validated BibleReference.
 * Validates chapter/verse numbers against real counts from the committed
 * Bible structure data — never returns a reference to a chapter/verse that
 * doesn't exist.
 */
export function parseReference(input: string): ParseResult | ParseError {
  const trimmed = input.trim();
  if (!trimmed) return { ok: false, reason: 'unparseable' };

  // Find the trailing "chapter[:/ ]verse[-verse]" numeric tail.
  // Examples matched at the end of string:
  //   "23"            -> chapter 23, no verse
  //   "3:16"          -> chapter 3, verse 16
  //   "3 16"          -> chapter 3, verse 16
  //   "13:4-7"        -> chapter 13, verses 4-7
  //   "13 4-7"        -> chapter 13, verses 4-7
  const tailMatch = trimmed.match(/(\d+)(?:[:\s]+(\d+)(?:\s*[-–]\s*(\d+))?)?\s*$/);
  if (!tailMatch || tailMatch.index === undefined) return { ok: false, reason: 'unparseable' };

  const bookPart = trimmed.slice(0, tailMatch.index).trim();
  if (!bookPart) return { ok: false, reason: 'unparseable' };

  const book = resolveBookName(bookPart);
  if (!book) return { ok: false, reason: 'unrecognized-book' };

  const chapter = parseInt(tailMatch[1], 10);
  const startVerse = tailMatch[2] ? parseInt(tailMatch[2], 10) : undefined;
  const endVerse = tailMatch[3] ? parseInt(tailMatch[3], 10) : startVerse;

  if (!isValidChapter(book.name, chapter)) return { ok: false, reason: 'invalid-chapter' };

  if (startVerse !== undefined) {
    if (!isValidVerse(book.name, chapter, startVerse)) return { ok: false, reason: 'invalid-verse' };
    if (endVerse !== undefined && endVerse !== startVerse && !isValidVerse(book.name, chapter, endVerse)) {
      return { ok: false, reason: 'invalid-verse' };
    }
    if (endVerse !== undefined && endVerse < startVerse) return { ok: false, reason: 'invalid-verse' };
  }

  return {
    ok: true,
    reference: { book, chapter, startVerse, endVerse },
  };
}

/**
 * How a book is named when a specific chapter follows it.
 *
 * The canonical book name is the plural "Psalms" — correct for the book as a
 * whole, wrong for any single one of them. Every citation convention, every
 * commentary, and every person typing into a search box writes "Psalm 23", not
 * "Psalms 23". This matters more than pedantry now that the reference is
 * rendered into the page's h1 ("What does Psalm 23 mean?"): that string is the
 * one an answer engine matches a question against.
 *
 * Psalms is the only book in the canon with this singular/plural split — the
 * other plural-looking names (Judges, Acts, Romans, Hebrews) are already
 * correct with a chapter after them.
 */
function citedBookName(ref: BibleReference): string {
  return ref.book.name === 'Psalms' ? 'Psalm' : ref.book.name;
}

/**
 * Same singular/plural correction as citedBookName, applied to an
 * already-formatted reference string. Exists for content generated before the
 * rule landed — see loadCommittedContent in verseContent.ts. Anchored to the
 * start so it can only ever touch the book name, never prose that happens to
 * mention the Psalms.
 */
export function citeReference(reference: string): string {
  return reference.replace(/^Psalms (?=\d)/, 'Psalm ');
}

/**
 * The same correction applied wherever a reference appears inside a short,
 * reference-bearing string — an FAQ question ("What does Psalms 23 mean?"), an
 * seoTitle, a meta description. Deliberately NOT applied to body prose: there
 * the plural can be legitimate ("the Psalms"), and a false correction in the
 * middle of a paragraph is worse than the inconsistency it fixes.
 *
 * Only rewrites "Psalms" immediately followed by a chapter number, which is
 * always a citation and never the book as a whole.
 */
export function citeInline(text: string): string {
  return text.replace(/\bPsalms (?=\d)/g, 'Psalm ');
}

/** Build the canonical display reference string, e.g. "1 Corinthians 13:4-7". */
export function formatReference(ref: BibleReference): string {
  const book = citedBookName(ref);
  if (ref.startVerse === undefined) {
    return `${book} ${ref.chapter}`;
  }
  if (ref.endVerse === undefined || ref.endVerse === ref.startVerse) {
    return `${book} ${ref.chapter}:${ref.startVerse}`;
  }
  return `${book} ${ref.chapter}:${ref.startVerse}-${ref.endVerse}`;
}

/** Build the URL slug for a reference, e.g. "1-corinthians-13-4-7". */
export function referenceToSlug(ref: BibleReference): string {
  const parts = [ref.book.slug, String(ref.chapter)];
  if (ref.startVerse !== undefined) {
    parts.push(String(ref.startVerse));
    if (ref.endVerse !== undefined && ref.endVerse !== ref.startVerse) {
      parts.push(String(ref.endVerse));
    }
  }
  return parts.join('-');
}

/** Parse a URL slug back into a validated BibleReference (or an error). */
export function slugToReference(slug: string): ParseResult | ParseError {
  const normalized = slug.trim().toLowerCase();
  if (!normalized) return { ok: false, reason: 'unparseable' };

  const bookSlug = bookSlugsByLengthDesc.find(
    (s) => normalized === s || normalized.startsWith(`${s}-`)
  );
  if (!bookSlug) return { ok: false, reason: 'unrecognized-book' };

  const book = bookBySlug.get(bookSlug)!;
  const remainder = normalized === bookSlug ? '' : normalized.slice(bookSlug.length + 1);
  if (!remainder) return { ok: false, reason: 'unparseable' };

  const numParts = remainder.split('-').filter(Boolean);
  if (numParts.length < 1 || numParts.length > 3 || numParts.some((p) => !/^\d+$/.test(p))) {
    return { ok: false, reason: 'unparseable' };
  }

  const chapter = parseInt(numParts[0], 10);
  const startVerse = numParts[1] ? parseInt(numParts[1], 10) : undefined;
  const endVerse = numParts[2] ? parseInt(numParts[2], 10) : startVerse;

  if (!isValidChapter(book.name, chapter)) return { ok: false, reason: 'invalid-chapter' };
  if (startVerse !== undefined) {
    if (!isValidVerse(book.name, chapter, startVerse)) return { ok: false, reason: 'invalid-verse' };
    if (endVerse !== undefined && endVerse !== startVerse && !isValidVerse(book.name, chapter, endVerse)) {
      return { ok: false, reason: 'invalid-verse' };
    }
    if (endVerse !== undefined && endVerse < startVerse) return { ok: false, reason: 'invalid-verse' };
  }

  return { ok: true, reference: { book, chapter, startVerse, endVerse } };
}

/** True if `input` (free text) parses as a valid, in-bounds reference. */
export function looksLikeReference(input: string): boolean {
  return parseReference(input).ok;
}

/** Total verse count covered by a reference (1 for a single verse, N for a range, full chapter count for chapter-only). */
export function referenceVerseCount(ref: BibleReference): number {
  if (ref.startVerse === undefined) return verseCount(ref.book.name, ref.chapter);
  return (ref.endVerse ?? ref.startVerse) - ref.startVerse + 1;
}
